import type { ReactNode } from "react";
import type {
  HeadersFunction,
  LinksFunction,
  LoaderFunctionArgs,
} from "@remix-run/node";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import apiClient, { apiHeaders } from "~/lib/api-client";
import { requireEmployeeSession } from "~/services/session.server";

import Loader from "./components/Loader/Loader";
import styles from "./tailwind.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;500;700&display=swap",
  },
];

export const headers: HeadersFunction = () => {
  const base: Record<string, string> = {
    "Referrer-Policy": "no-referrer",
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "Permissions-Policy":
      "geolocation=(), microphone=(), camera=(), payment=(), usb=()",
  };

  if (process.env.NODE_ENV === "production") {
    // Prefer setting HSTS at the edge (LB/CDN), but this provides a safe baseline.
    base["Strict-Transport-Security"] = "max-age=31536000; includeSubDomains";
  }

  return base;
};

type EmployeeSummary = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
};

export async function loader({ request }: LoaderFunctionArgs) {
  let session: Awaited<ReturnType<typeof requireEmployeeSession>>;

  try {
    session = await requireEmployeeSession(request);
  } catch {
    return { employeeSummary: null as EmployeeSummary | null };
  }

  const { data } = await apiClient
    .GET("/api/v1/employees/me", {
      headers: apiHeaders({ token: session.token }),
    })
    .catch(() => ({ data: null as { employee?: EmployeeSummary } | null }));

  const employee = data?.employee;

  if (!employee) {
    const [firstName, ...lastNameParts] = (session.name || "").split(" ");

    return {
      employeeSummary: {
        id: session.id || "",
        firstName: firstName || "",
        lastName: lastNameParts.join(" ") || "",
        role: session.role || "",
      } as EmployeeSummary,
    };
  }

  return {
    employeeSummary: {
      id: employee.id || "",
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      role: employee.role || "",
    } as EmployeeSummary,
  };
}

export function Layout({ children }: { children: React.ReactNode }): ReactNode {
  return (
    <html lang="fi">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="build-timestamp" />
        <style data-fullcalendar />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Loader />
      </body>
    </html>
  );
}

function App(): ReactNode {
  return <Outlet />;
}

export default withSentry(App);

export function ErrorBoundary(): ReactNode {
  const error = useRouteError();

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("Error in error boundary", { error });
  }

  if (isRouteErrorResponse(error)) {
    if (process.env.NODE_ENV !== "development") {
      return (
        <div className="min-h-screen bg-background">
          <main className="flex-1 p-6">
            <h1>{error.status}</h1>
            <p>Tapahtui virhe.</p>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <main className="flex-1 p-6">
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>
            {typeof error.data === "string"
              ? error.data
              : JSON.stringify(error.data)}
          </p>
        </main>
      </div>
    );
  }

  captureRemixErrorBoundaryError(error);

  return <h1>Error!</h1>;
}
